import { __assign, __extends } from "tslib";
import { equal } from '@wry/equality';
import { ApolloError } from "../../errors/index.js";
import { NetworkStatus } from "../../core/index.js";
import { DocumentType } from "../parser/index.js";
import { OperationData } from "./OperationData.js";
var QueryData = function (_super) {
  __extends(QueryData, _super);
  function QueryData(_a) {
    var options = _a.options,
      context = _a.context,
      onNewData = _a.onNewData;
    var _this = _super.call(this, options, context) || this;
    _this.runLazy = false;
    _this.previous = Object.create(null);
    _this.runLazyQuery = function (options) {
      _this.cleanup();
      _this.runLazy = true;
      _this.lazyOptions = options;
      _this.onNewData();
    };
    _this.obsRefetch = function (variables) {
      var _a;
      return (_a = _this.currentObservable) === null || _a === void 0 ? void 0 : _a.refetch(variables);
    };
    _this.obsFetchMore = function (fetchMoreOptions) {
      var _a;
      return (_a = _this.currentObservable) === null || _a === void 0 ? void 0 : _a.fetchMore(fetchMoreOptions);
    };
    _this.obsUpdateQuery = function (mapFn) {
      var _a;
      return (_a = _this.currentObservable) === null || _a === void 0 ? void 0 : _a.updateQuery(mapFn);
    };
    _this.obsStartPolling = function (pollInterval) {
      var _a;
      (_a = _this.currentObservable) === null || _a === void 0 ? void 0 : _a.startPolling(pollInterval);
    };
    _this.obsStopPolling = function () {
      var _a;
      (_a = _this.currentObservable) === null || _a === void 0 ? void 0 : _a.stopPolling();
    };
    _this.obsSubscribeToMore = function (options) {
      var _a;
      return (_a = _this.currentObservable) === null || _a === void 0 ? void 0 : _a.subscribeToMore(options);
    };
    _this.onNewData = onNewData;
    return _this;
  }
  QueryData.prototype.execute = function () {
    this.refreshClient();
    var _a = this.getOptions(),
      skip = _a.skip,
      query = _a.query;
    if (skip || query !== this.previous.query) {
      this.removeQuerySubscription();
      this.removeObservable(!skip);
      this.previous.query = query;
    }
    this.updateObservableQuery();
    return this.getExecuteSsrResult() || this.getExecuteResult();
  };
  QueryData.prototype.executeLazy = function () {
    return !this.runLazy ? [this.runLazyQuery, {
      loading: false,
      networkStatus: NetworkStatus.ready,
      called: false,
      data: undefined
    }] : [this.runLazyQuery, this.execute()];
  };
  QueryData.prototype.fetchData = function () {
    var _this = this;
    var options = this.getOptions();
    if (options.skip || options.ssr === false) return false;
    return new Promise(function (resolve) {
      return _this.startQuerySubscription(resolve);
    });
  };
  QueryData.prototype.afterExecute = function (_a) {
    var _b = _a === void 0 ? {} : _a,
      _c = _b.lazy,
      lazy = _c === void 0 ? false : _c;
    this.isMounted = true;
    var options = this.getOptions();
    if (this.currentObservable && !this.ssrInitiated()) {
      this.startQuerySubscription();
    }
    if (!lazy || this.runLazy) {
      this.handleErrorOrCompleted();
    }
    this.previousOptions = options;
    return this.unmount.bind(this);
  };
  QueryData.prototype.cleanup = function () {
    this.removeQuerySubscription();
    this.removeObservable(true);
    delete this.previous.result;
  };
  QueryData.prototype.getOptions = function () {
    var options = _super.prototype.getOptions.call(this);
    if (this.lazyOptions) {
      options.variables = __assign(__assign({}, options.variables), this.lazyOptions.variables);
      options.context = __assign(__assign({}, options.context), this.lazyOptions.context);
    }
    if (this.runLazy) {
      delete options.skip;
    }
    return options;
  };
  QueryData.prototype.ssrInitiated = function () {
    return this.context && this.context.renderPromises;
  };
  QueryData.prototype.getExecuteSsrResult = function () {
    var _a = this.getOptions(),
      ssr = _a.ssr,
      skip = _a.skip;
    var ssrDisabled = ssr === false;
    var fetchDisabled = this.refreshClient().client.disableNetworkFetches;
    var ssrLoading = __assign({
      loading: true,
      networkStatus: NetworkStatus.loading,
      called: true,
      data: undefined,
      stale: false,
      client: this.client
    }, this.observableQueryFields());
    if (ssrDisabled && (this.ssrInitiated() || fetchDisabled)) {
      this.previous.result = ssrLoading;
      return ssrLoading;
    }
    if (this.ssrInitiated()) {
      var result = this.getExecuteResult() || ssrLoading;
      if (result.loading && !skip) {
        this.context.renderPromises.addQueryPromise(this, function () {
          return null;
        });
      }
      return result;
    }
  };
  QueryData.prototype.prepareObservableQueryOptions = function () {
    var options = this.getOptions();
    this.verifyDocumentType(options.query, DocumentType.Query);
    var displayName = options.displayName || 'Query';
    if (this.ssrInitiated() && (options.fetchPolicy === 'network-only' || options.fetchPolicy === 'cache-and-network')) {
      options.fetchPolicy = 'cache-first';
    }
    return __assign(__assign({}, options), {
      displayName: displayName,
      context: options.context
    });
  };
  QueryData.prototype.initializeObservableQuery = function () {
    if (this.ssrInitiated()) {
      this.currentObservable = this.context.renderPromises.getSSRObservable(this.getOptions());
    }
    if (!this.currentObservable) {
      var observableQueryOptions = this.prepareObservableQueryOptions();
      this.previous.observableQueryOptions = __assign(__assign({}, observableQueryOptions), {
        children: void 0
      });
      this.currentObservable = this.refreshClient().client.watchQuery(__assign({}, observableQueryOptions));
      if (this.ssrInitiated()) {
        this.context.renderPromises.registerSSRObservable(this.currentObservable, observableQueryOptions);
      }
    }
  };
  QueryData.prototype.updateObservableQuery = function () {
    if (!this.currentObservable) {
      this.initializeObservableQuery();
      return;
    }
    var newObservableQueryOptions = __assign(__assign({}, this.prepareObservableQueryOptions()), {
      children: void 0
    });
    if (this.getOptions().skip) {
      this.previous.observableQueryOptions = newObservableQueryOptions;
      return;
    }
    if (!equal(newObservableQueryOptions, this.previous.observableQueryOptions)) {
      this.previous.observableQueryOptions = newObservableQueryOptions;
      this.currentObservable.setOptions(newObservableQueryOptions).catch(function () {});
    }
  };
  QueryData.prototype.startQuerySubscription = function (onNewData) {
    var _this = this;
    if (onNewData === void 0) {
      onNewData = this.onNewData;
    }
    if (this.currentSubscription || this.getOptions().skip) return;
    this.currentSubscription = this.currentObservable.subscribe({
      next: function (_a) {
        var loading = _a.loading,
          networkStatus = _a.networkStatus,
          data = _a.data;
        var previousResult = _this.previous.result;
        if (previousResult && previousResult.loading === loading && previousResult.networkStatus === networkStatus && equal(previousResult.data, data)) {
          return;
        }
        onNewData();
      },
      error: function (error) {
        _this.resubscribeToQuery();
        if (!error.hasOwnProperty('graphQLErrors')) throw error;
        var previousResult = _this.previous.result;
        if (previousResult && previousResult.loading || !equal(error, _this.previous.error)) {
          _this.previous.error = error;
          onNewData();
        }
      }
    });
  };
  QueryData.prototype.resubscribeToQuery = function () {
    this.removeQuerySubscription();
    var currentObservable = this.currentObservable;
    if (currentObservable) {
      var last = currentObservable["last"];
      try {
        currentObservable.resetLastResults();
        this.startQuerySubscription();
      } finally {
        currentObservable["last"] = last;
      }
    }
  };
  QueryData.prototype.getExecuteResult = function () {
    var result = this.observableQueryFields();
    var options = this.getOptions();
    if (options.skip) {
      result = __assign(__assign({}, result), {
        data: undefined,
        error: undefined,
        loading: false,
        networkStatus: NetworkStatus.ready,
        called: true
      });
    } else if (this.currentObservable) {
      var currentResult = this.currentObservable.getCurrentResult();
      var data = currentResult.data,
        loading = currentResult.loading,
        partial = currentResult.partial,
        networkStatus = currentResult.networkStatus,
        errors = currentResult.errors;
      var error = currentResult.error;
      if (errors && errors.length > 0) {
        error = new ApolloError({
          graphQLErrors: errors
        });
      }
      result = __assign(__assign({}, result), {
        data: data,
        loading: loading,
        networkStatus: networkStatus,
        error: error,
        called: true
      });
      if (loading) {} else if (error) {
        Object.assign(result, {
          data: (this.currentObservable.getLastResult() || {}).data
        });
      } else {
        var fetchPolicy = this.currentObservable.options.fetchPolicy;
        var partialRefetch = options.partialRefetch;
        if (partialRefetch && partial && (!data || Object.keys(data).length === 0) && fetchPolicy !== 'cache-only') {
          Object.assign(result, {
            loading: true,
            networkStatus: NetworkStatus.loading
          });
          result.refetch();
          return result;
        }
      }
    }
    result.client = this.client;
    this.setOptions(options, true);
    var previousResult = this.previous.result;
    this.previous.loading = previousResult && previousResult.loading || false;
    result.previousData = previousResult && (previousResult.data || previousResult.previousData);
    this.previous.result = result;
    this.currentObservable && this.currentObservable.resetQueryStoreErrors();
    return result;
  };
  QueryData.prototype.handleErrorOrCompleted = function () {
    if (!this.currentObservable || !this.previous.result) return;
    var _a = this.previous.result,
      data = _a.data,
      loading = _a.loading,
      error = _a.error;
    if (!loading) {
      var _b = this.getOptions(),
        query = _b.query,
        variables = _b.variables,
        onCompleted = _b.onCompleted,
        onError = _b.onError,
        skip = _b.skip;
      if (this.previousOptions && !this.previous.loading && equal(this.previousOptions.query, query) && equal(this.previousOptions.variables, variables)) {
        return;
      }
      if (onCompleted && !error && !skip) {
        onCompleted(data);
      } else if (onError && error) {
        onError(error);
      }
    }
  };
  QueryData.prototype.removeQuerySubscription = function () {
    if (this.currentSubscription) {
      this.currentSubscription.unsubscribe();
      delete this.currentSubscription;
    }
  };
  QueryData.prototype.removeObservable = function (andDelete) {
    if (this.currentObservable) {
      this.currentObservable["tearDownQuery"]();
      if (andDelete) {
        delete this.currentObservable;
      }
    }
  };
  QueryData.prototype.observableQueryFields = function () {
    var _a;
    return {
      variables: (_a = this.currentObservable) === null || _a === void 0 ? void 0 : _a.variables,
      refetch: this.obsRefetch,
      fetchMore: this.obsFetchMore,
      updateQuery: this.obsUpdateQuery,
      startPolling: this.obsStartPolling,
      stopPolling: this.obsStopPolling,
      subscribeToMore: this.obsSubscribeToMore
    };
  };
  return QueryData;
}(OperationData);
export { QueryData };